"use client";

import { Link } from "@/navigation";
import { useEffect, useState, useCallback } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { ProductCategoriesListQuery } from "@/gql/graphql";
import { defaultLocale } from "@/config";

type CategoryEdge = ProductCategoriesListQuery["categories"]["edges"][0];

const CategoryItem = ({
	locale,
	item,
	level = 0,
	currentSlug,
	expandedCategories,
	toggleCategory,
	t,
}: {
	locale?: string;
	item: CategoryEdge;
	level?: number;
	currentSlug: string;
	expandedCategories: string[];
	toggleCategory: (id: string) => void;
	t: any;
}) => {
	const hasChildren = item.node.children?.edges?.length > 0;
	const isExpanded = expandedCategories.includes(item.node.id);
	const isActive = item.node.slug === currentSlug;
	const categoryName =
		locale === defaultLocale ? item.node.name : item.node.translation?.name || item.node.name;
	return (
		<div className="relative">
			<div
				className={`group flex items-center justify-between rounded-lg px-3 py-2
					transition-colors duration-150
					${isActive ? "bg-black text-white" : "text-neutral-600 hover:bg-neutral-100"}
					${isExpanded && !isActive ? "bg-neutral-50" : ""}`}
				style={{ paddingLeft: `${level * 0.65 + 0.75}rem` }}
			>
				<div className="flex min-w-0 items-center gap-2">
					{hasChildren && (
						<button
							type="button"
							aria-label={
								isExpanded
									? t("collapseCategory" as any, { name: categoryName })
									: t("expandCategory" as any, { name: categoryName })
							}
							onClick={() => toggleCategory(item.node.id)}
							className={`flex h-5 w-5 shrink-0 items-center justify-center rounded
								transition-colors duration-150
								${isActive ? "bg-white/20 hover:bg-white/30" : "bg-neutral-100 hover:bg-neutral-200"}
								${isExpanded && !isActive ? "bg-neutral-200" : ""}`}
						>
							<div
								className={`transform transition-transform duration-200 
								${isExpanded ? "rotate-180" : "rotate-0"}`}
							>
								<ChevronDown
									className={`h-3 w-3 
									${isActive ? "text-white" : "text-neutral-500"}`}
								/>
							</div>
						</button>
					)}
					<Link
						href={`/products/${item.node.slug}`}
						title={categoryName}
						className={`min-w-0 truncate text-[13px] leading-5 transition-colors duration-150 
							${isActive ? "font-medium text-white" : "font-normal text-neutral-600 group-hover:text-black"}`}
					>
						{categoryName}
					</Link>
				</div>

				{/* <div className={`text-xs ${isActive ? "text-blue-400" : "text-gray-400"}`}>
					({item?.node?.products?.totalCount || 0})
				</div> */}
			</div>

			{hasChildren && (
				<div
					className={`grid transition-all duration-300 ease-in-out
						${isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
				>
					<div className="overflow-hidden">
						<div className={`space-y-0.5 ${level < 2 ? "ml-4 border-l border-gray-100 pl-2" : ""}`}>
							{item.node.children?.edges.map((child: CategoryEdge) => (
								<CategoryItem
									key={child.node.id}
									locale={locale}
									item={child}
									level={level + 1}
									currentSlug={currentSlug}
									expandedCategories={expandedCategories}
									toggleCategory={toggleCategory}
									t={t}
								/>
							))}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

type CategoriesProps = ProductCategoriesListQuery["categories"];

const findParentCategoryIds = (
	categories: CategoryEdge[] | undefined,
	targetSlug: string,
	parentIds: string[] = [],
): string[] => {
	if (!categories) return [];

	return categories.reduce((acc, category) => {
		if (category.node.slug === targetSlug) return parentIds;

		if (category.node.children?.edges?.length) {
			const result = findParentCategoryIds(category.node.children.edges, targetSlug, [
				...parentIds,
				category.node.id,
			]);
			if (result.length) return result;
		}
		return acc;
	}, [] as string[]);
};

const getSiblingCategories = (categories: CategoryEdge[] | undefined, targetId: string): string[] => {
	if (!categories) return [];

	const findParent = (
		items: CategoryEdge[],
		id: string,
	): { parent: CategoryEdge | null; siblings: string[] } => {
		for (const item of items) {
			// 如果是顶级类别
			if (item.node.id === id) {
				return {
					parent: null,
					siblings: items.map((cat) => cat.node.id).filter((catId) => catId !== id),
				};
			}

			// 在子类别中查找
			if (item.node.children?.edges) {
				for (const child of item.node.children.edges) {
					if (child.node.id === id) {
						return {
							parent: item,
							siblings: item.node.children.edges.map((cat) => cat.node.id).filter((catId) => catId !== id),
						};
					}
				}
				const result = findParent(item.node.children.edges, id);
				if (result.parent || result.siblings.length) return result;
			}
		}
		return { parent: null, siblings: [] };
	};

	return findParent(categories, targetId).siblings;
};

const Categories = ({ locale, categoriesData }: { locale?: string; categoriesData: CategoriesProps }) => {
	const t = useTranslations("blog");
	const { slug: currentSlug } = useParams();
	const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

	useEffect(() => {
		if (currentSlug && categoriesData) {
			setExpandedCategories(findParentCategoryIds(categoriesData.edges, currentSlug as string));
		}
	}, [locale, currentSlug, categoriesData]);

	const toggleCategory = useCallback(
		(categoryId: string) => {
			setExpandedCategories((prev) =>
				prev.includes(categoryId)
					? prev.filter((id) => id !== categoryId)
					: [
							...prev.filter((id) => !getSiblingCategories(categoriesData.edges, categoryId).includes(id)),
							categoryId,
						],
			);
		},
		[categoriesData],
	);

	return (
		<div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
			<div className="border-b border-neutral-200 px-4 py-3">
				<h3 className="text-sm font-semibold text-black">{t("Categories")}</h3>
			</div>
			<div className="max-h-[70vh] overflow-y-auto p-3">
				<div className="space-y-1">
					{categoriesData?.edges?.map((item) => (
						<CategoryItem
							key={item.node.id}
							locale={locale}
							item={item}
							currentSlug={currentSlug as string}
							expandedCategories={expandedCategories}
							toggleCategory={toggleCategory}
							t={t}
						/>
					))}
				</div>
			</div>
		</div>
	);
};

export default Categories;
